import type { ReviewItem } from '@mybusinessmate/schema';
import { weekdayDay } from './task-display';

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** How long a draft has waited, as the queue says it ("40 min", "2 h", "1 day"); a day or more is flagged late. */
export function waitingFor(since: string, now: Date = new Date()): { text: string; late: boolean } {
  const elapsed = Math.max(0, now.getTime() - new Date(since).getTime());
  if (elapsed < HOUR) return { text: `${Math.max(1, Math.floor(elapsed / MINUTE))} min`, late: false };
  if (elapsed < DAY) return { text: `${Math.floor(elapsed / HOUR)} h`, late: false };
  const days = Math.floor(elapsed / DAY);
  return { text: `${days} ${days === 1 ? 'day' : 'days'}`, late: true };
}

const dayMonth = new Intl.DateTimeFormat('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });

/** "Publishes Thu 23 Oct" for something that goes out on a date; "Due Thu 23 Oct" for the team's own deadline. */
export function clientDateText(item: Pick<ReviewItem, 'clientDate' | 'clientDateKind' | 'fix'>): string {
  // A fix to something the client already approved is live: there is no date to wait for.
  if (item.fix?.clientApproved) return 'Live · client approved';
  if (!item.clientDate || !item.clientDateKind) return '—';
  const when =
    item.clientDateKind === 'publishes' ? dayMonth.format(new Date(item.clientDate)) : weekdayDay(item.clientDate);
  return `${item.clientDateKind === 'publishes' ? 'Publishes' : 'Due'} ${when}`;
}

/** The tag beside a title: what makes this draft different from a plain next version. */
export function reviewFlag(item: Pick<ReviewItem, 'firstDraft' | 'clientRevision' | 'fix'>): string | null {
  if (item.fix) return `Draft fix · ${item.fix.fields.join(', ')}`;
  if (item.clientRevision) return `Client revision ${item.clientRevision.number} of ${item.clientRevision.of}`;
  return item.firstDraft ? 'First draft' : null;
}
